/**
 * FerryGate Widget — entry point IIFE.
 *
 * Custom element `<ferrygate-booking>` autonome cross-origin. Tout est
 * provide/inject localement au composant — pas de Pinia, pas de Vue Router,
 * pas de vue-i18n. Architecture intentionnellement minimaliste : 1 store
 * reactive, 1 client api fetch, 1 router state machine, 1 i18n dict.
 *
 * Attributs :
 *   - `api-key` (obligatoire) — clé widget `wgt_*`
 *   - `lang` (optionnel) — `fr|en` (défaut : détection navigateur ou `fr`)
 *
 * URL backend : injectée au build via `VITE_FERRYGATE_API_URL`. Fallback prod.
 */

import { defineCustomElement, h, provide, useHost } from 'vue'
import Shell from './components/Shell.vue'
import { STORE_KEY, createStore } from './store'
import { API_KEY, WidgetApi } from './api'
import { I18N_KEY, createI18n } from './i18n'
import { ROUTER_KEY, createRouter } from './router'
import { TRACKER_KEY, createTracker } from './tracker'
import { parseWidgetToken } from './utils/token'
import { Direction } from '../../shared/types/enums'
import type { Journey } from '../../shared/types'
// CSS inline pour le shadow DOM (Vite remplace ?inline par la string au build)
// @ts-expect-error résolu par Vite
import styles from './styles.css?inline'

const API_BASE_URL =
  (import.meta.env.VITE_FERRYGATE_API_URL as string | undefined)
  || 'https://api.ferrygate.com'

// Base URL du SaaS FerryGate cible pour la redirection mode Agent
// (page de confirmation `/{lang}/app/search/ferry/confirmation/{id}`).
// Injectée au build, fallback prod.
const SAAS_BASE_URL =
  (import.meta.env.VITE_FERRYGATE_SAAS_URL as string | undefined)
  || 'https://app.ferrygate.com'

// Résout l'URL de la page de checkout B2C de l'hôte (tech-agnostique, zéro-config) :
//   1. attribut `checkout-url` explicite (override, ex. checkout cross-origin)
//   2. sinon : origine de la page hôte (`window.location.origin`) + `checkout-path`
//      (défaut `/checkout`) — `<ferrygate-booking>` est un web component, il tourne
//      dans le `window` de l'hôte, donc l'origine est toujours celle du site intégrateur.
// Identique pour Vue/React/HTML/… : aucune config ni token spécifique par environnement.
export function resolveCheckoutUrl(explicit: string, path: string): string | null {
  const override = (explicit || '').trim()
  if (override) return override
  if (typeof window !== 'undefined' && window.location?.origin) {
    const p = (path || '').trim() || '/checkout'
    return `${window.location.origin}${p.startsWith('/') ? p : `/${p}`}`
  }
  return null
}

const FerryBooking = defineCustomElement({
  name: 'FerryBooking',
  shadowRoot: true,
  props: {
    apiKey: { type: String, default: '' },
    lang: { type: String, default: '' },
    checkoutUrl: { type: String, default: '' },
    checkoutPath: { type: String, default: '' },
    journeyId: { type: String, default: '' },
    beintravelUrl: { type: String, default: '' },
  },
  setup(props: { apiKey: string; lang: string; checkoutUrl: string; checkoutPath: string; journeyId: string; beintravelUrl: string }) {
    const apiKey = (props.apiKey || '').trim()
    const lang = resolveLocale(props.lang)

    if (!apiKey) {
      console.error('[FerryGate Widget] Missing api-key attribute')
    }

    const meta = parseWidgetToken(apiKey)
    if (meta) {
      console.info('[FerryGate Widget] token hydraté', { orgId: meta.orgId, v: meta.version, mode: meta.mode })
    }

    const store = createStore()
    // Mode dérivé du token. Fallback `agent` pour les tokens legacy sans `m`
    // (préserve le comportement historique avant la migration widget_mode).
    store.mode = meta?.mode ?? 'agent'
    // checkout_url : dérivé de l'origine de la page hôte par défaut (zéro-config,
    // identique quelle que soit la techno de l'intégrateur). Voir resolveCheckoutUrl.
    store.checkoutUrl = resolveCheckoutUrl(props.checkoutUrl, props.checkoutPath)
    store.saasBaseUrl = SAAS_BASE_URL
    // Endpoint d'enregistrement beintravel (attribut `beintravel-url`). Optionnel :
    // vide → étape beintravel ignorée (hôtes non-beintravel inchangés).
    store.beintravelUrl = (props.beintravelUrl || '').trim() || null

    const api = new WidgetApi({ apiKey, baseUrl: API_BASE_URL, orgId: meta?.orgId, language: lang })
    const i18n = createI18n(lang)
    const router = createRouter()

    provide(STORE_KEY, store)
    provide(API_KEY, api)
    provide(I18N_KEY, i18n)
    provide(ROUTER_KEY, router)

    // Tracker du funnel : émet `fg:event` sur l'élément hôte à chaque jalon
    // (recherche → résultats → option → checkout). useHost() retourne l'élément
    // <ferrygate-booking> réel ; l'événement franchit le shadow DOM (composed).
    const host = useHost()
    const tracker = createTracker(host)
    provide(TRACKER_KEY, tracker)

    // Pré-sélection optionnelle d'une traversée via l'attribut `journey-id`
    // (landing pages mono-route) : le widget s'ouvre avec la traversée déjà
    // choisie, l'user n'a plus qu'à choisir ses dates. Sans l'attribut → no-op,
    // comportement historique strictement inchangé.
    const presetJourneyId = (props.journeyId || '').trim()

    // Pre-load les catalogues en background (journeys + vehicle brands) pour
    // que les drawers correspondants s'ouvrent instantanément. Silencieux sur
    // erreur — les drawers ont un fallback fetch. Si `journey-id` est fourni, on
    // enchaîne la pré-sélection une fois le catalogue chargé.
    void api.listJourneys(lang)
      .then((list) => {
        store.journeyCatalog = list
        if (presetJourneyId) return prefillJourney(api, store, list, presetJourneyId)
      })
      .catch((err) => {
        tracker.emit('widget_error', {
          code: 'CATALOG_LOAD_FAILED',
          catalog: 'journeys',
          message: err instanceof Error ? err.message : 'journeys load failed',
          current_step: router.currentStep.value,
        })
      })

    // Pre-load brands PUIS modèles des marques préférées. Chaîné pour qu'au
    // moment où l'user atteint l'étape `model`, les modèles soient déjà en
    // cache (`store.modelsByBrand[brandId]`) — pas de spinner intermédiaire.
    void api.listVehicleBrands()
      .then(async (brands) => {
        store.brandCatalog = brands
        await prefetchPreferredVehicleModels(api, store, brands)
      })
      .catch((err) => {
        tracker.emit('widget_error', {
          code: 'CATALOG_LOAD_FAILED',
          catalog: 'brands',
          message: err instanceof Error ? err.message : 'brands load failed',
          current_step: router.currentStep.value,
        })
      })

    return () => h(Shell)
  },
  styles: [styles as string],
})

if (typeof customElements !== 'undefined' && !customElements.get('ferrygate-booking')) {
  customElements.define('ferrygate-booking', FerryBooking)
  injectGlobalStyles()
}

/**
 * Le drawer est rendu via Teleport dans `document.body` (hors shadow DOM)
 * pour qu'il s'étende sur toute la viewport navigateur, peu importe la taille
 * du container hôte. On doit donc injecter les styles aussi dans `document.head`
 * — sinon le drawer téléporté n'est pas stylé. Idempotent (one-shot).
 */
function injectGlobalStyles(): void {
  if (typeof document === 'undefined') return
  if (document.getElementById('fg-widget-styles')) return
  const tag = document.createElement('style')
  tag.id = 'fg-widget-styles'
  tag.textContent = styles as string
  document.head.appendChild(tag)
}

/**
 * Pré-charge les modèles des marques marquées `prefer: "Yes"` par le backend.
 * Fallback sur les 10 premières si aucun flag prefer. Stockage dans
 * `store.modelsByBrand[brandId]` (cache long-lived).
 *
 * Silencieux : un fetch qui plante n'empêche pas les autres ; au pire, le
 * drawer relancera le fetch au click.
 */
const PREFETCH_FALLBACK_TOP_N = 10
async function prefetchPreferredVehicleModels(
  api: WidgetApi,
  store: ReturnType<typeof createStore>,
  brands: Array<{ id: string; prefer?: string | boolean }>,
): Promise<void> {
  const isPreferred = (b: { prefer?: string | boolean }): boolean => {
    if (typeof b.prefer === 'boolean') return b.prefer
    return String(b.prefer ?? '').toLowerCase() === 'yes'
  }
  const preferred = brands.filter(isPreferred)
  const targets = preferred.length > 0
    ? preferred
    : brands.slice(0, PREFETCH_FALLBACK_TOP_N)
  const toFetch = targets.filter((b) => b.id && !store.modelsByBrand[b.id])

  await Promise.all(toFetch.map(async (b) => {
    try {
      store.modelsByBrand[b.id] = await api.listVehicleModels(b.id)
    } catch {
      // silent : fallback au click sur la marque
    }
  }))
}

/**
 * Pré-sélectionne une traversée à partir de son `journeyId` (attribut
 * `journey-id`), en répliquant exactement la logique de `JourneysDrawer.pick` :
 * on regroupe toutes les compagnies desservant la même paire origin/destination
 * et on charge leurs variants (Outward + Return) pour que le calendrier fusionne
 * toutes les dates disponibles. Silencieux sur erreur — l'user peut toujours
 * choisir manuellement dans le drawer.
 */
async function prefillJourney(
  api: WidgetApi,
  store: ReturnType<typeof createStore>,
  catalog: Journey[],
  journeyId: string,
): Promise<void> {
  const target = catalog.find((j) => j.id === journeyId)
  if (!target) return

  const sameRoute = catalog.filter(
    (j) =>
      (j.direction === Direction.Outward || !j.direction)
      && j.origin?.port_code === target.origin?.port_code
      && j.destination?.port_code === target.destination?.port_code,
  )
  const ids = sameRoute.map((j) => j.id).filter((id): id is string => !!id)
  if (ids.length === 0 || store.loadingJourney) return

  store.loadingJourney = true
  store.search.request = {}
  try {
    const results = await Promise.all(ids.map((id) => api.getJourneyVariants(id)))
    store.search.journeys = results.flat()
  } catch {
    store.search.journeys = []
  } finally {
    store.loadingJourney = false
  }
}

function resolveLocale(attr: string | null | undefined): string {
  const supported = ['fr', 'en']
  const raw = (attr || '').toLowerCase().split('-')[0]
  if (supported.includes(raw)) return raw
  if (typeof navigator !== 'undefined') {
    const nav = (navigator.language || '').toLowerCase().split('-')[0]
    if (supported.includes(nav)) return nav
  }
  return 'fr'
}
